/* Obtino — Tester Box builder
   Step 1: customer picks a size (a variant of the box product, e.g. 3ml / 5ml).
   Step 2: customer picks N fragrances from a curated collection.
   Result: ONE line item — the box product in the chosen size — with the
   fragrances attached as line item properties (Duft 1..N). */
if (!customElements.get('obtino-tester-box')) {
  customElements.define(
    'obtino-tester-box',
    class ObtinoTesterBox extends HTMLElement {
      connectedCallback() {
        this.slots = parseInt(this.dataset.slots, 10) || 5;
        this.poolHandle = this.dataset.pool || '';
        this.poolCount = parseInt(this.dataset.poolCount, 10) || 0;
        this.propPrefix = this.dataset.propPrefix || 'Duft';
        this.filterBy = this.dataset.filterBy === 'tag' ? 'tag' : 'vendor';
        this.moneyFormat = this.dataset.moneyFormat || '{{amount_with_comma_separator}} kr';

        this.grid = this.querySelector('[data-otb-grid]');
        this.step2 = this.querySelector('[data-otb-step2]');
        this.submit = this.querySelector('[data-otb-submit]');
        this.submitText = this.querySelector('[data-otb-submit-text]');
        this.slotsHost = this.querySelector('[data-otb-slots]');
        this.searchInput = this.querySelector('[data-otb-search]');
        this.filtersHost = this.querySelector('[data-otb-filters]');
        this.emptyEl = this.querySelector('[data-otb-empty]');
        this.clearBtn = this.querySelector('[data-otb-clear]');
        this.errorEl = this.querySelector('[data-otb-error]');
        this.priceHost = this.querySelector('[data-otb-price]');
        this.form = this.querySelector('form');

        this.sizes = {};
        this.querySelectorAll('[data-otb-size]').forEach((btn) => {
          this.sizes[btn.dataset.otbSize] = {
            key: btn.dataset.otbSize,
            label: btn.dataset.label || btn.dataset.otbSize,
            unit: parseInt(btn.dataset.unit, 10) || 0,
            price: btn.dataset.price || '',
          };
        });

        this.size = null;
        this.selected = [];
        this.activeTag = '';
        this.query = '';

        this.bind();
        this.buildFilters();
        this.topUp();

        const pre = this.dataset.preselect;
        if (pre && this.sizes[pre]) this.chooseSize(pre);
        else this.sync();
      }

      /* ---------- size step ---------- */

      chooseSize(key) {
        if (!this.sizes[key]) return;
        this.size = key;
        this.querySelectorAll('[data-otb-size]').forEach((b) =>
          b.setAttribute('aria-pressed', b.dataset.otbSize === key ? 'true' : 'false')
        );
        if (this.step2) this.step2.hidden = false;
        // drop any picks the new size doesn't carry, keep the rest
        this.selected = this.selected.filter((s) => s.variants && s.variants[key]);
        this.applyFilter();
        this.sync();
      }

      /* ---------- data ---------- */

      get cards() {
        return Array.from(this.grid.querySelectorAll('.otb__card'));
      }

      cardData(card) {
        const parseMap = (raw) => {
          const out = {};
          (raw || '').split('|').forEach((pair) => {
            const i = pair.lastIndexOf(':');
            if (i > 0) out[pair.slice(0, i).trim()] = pair.slice(i + 1).trim();
          });
          return out;
        };
        const variants = parseMap(card.dataset.variants);
        const prices = parseMap(card.dataset.prices);
        const avail = parseMap(card.dataset.avail);
        return {
          handle: card.dataset.handle,
          variants: variants,
          prices: prices,
          avail: avail,
          title: card.dataset.title,
          vendor: card.dataset.vendor,
          image: card.dataset.image || '',
        };
      }

      /* Liquid renders at most 50 products per section; pull the rest client-side. */
      topUp() {
        const rendered = this.cards.length;
        if (!this.poolHandle || this.poolCount <= rendered) return;
        fetch(`/collections/${this.poolHandle}/products.json?limit=250`)
          .then((r) => (r.ok ? r.json() : null))
          .then((data) => {
            if (!data || !data.products) return;
            const have = new Set(this.cards.map((c) => c.dataset.handle));
            const frag = document.createDocumentFragment();
            data.products.forEach((p) => {
              if (have.has(p.handle)) return;
              const map = {};
              const pmap = {};
              const amap = {};
              p.variants.forEach((v) => {
                const k = String(v.title).trim().toLowerCase().replace(/\s+/g, '');
                map[k] = v.id;
                pmap[k] = Math.round(parseFloat(v.price) * 100);
                amap[k] = v.available ? '1' : '0';
              });
              frag.appendChild(
                this.buildCard({
                  handle: p.handle,
                  variants: map,
                  prices: pmap,
                  avail: amap,
                  vendor: p.vendor || '',
                  title: this.cleanTitle(p.title, p.vendor),
                  image: p.images && p.images[0] ? p.images[0].src : '',
                  tags: (p.tags || []).join('|'),
                })
              );
            });
            this.grid.appendChild(frag);
            this.buildFilters();
            this.applyFilter();
          })
          .catch(() => {});
      }

      cleanTitle(title, vendor) {
        let t = String(title || '');
        t = t.replace(/\s*[-–—]\s*Sample\s*$/i, '');
        if (vendor) {
          const v = vendor.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          t = t.replace(new RegExp('^' + v + '\\s*[-–—]\\s*', 'i'), '');
          t = t.replace(new RegExp('\\s*[-–—]\\s*' + v + '\\s*$', 'i'), '');
        }
        return t.trim();
      }

      buildCard(d) {
        const card = document.createElement('div');
        card.className = 'otb__card';
        card.dataset.handle = d.handle;
        const enc = (m) => Object.keys(m || {}).map((k) => k + ':' + m[k]).join('|');
        card.dataset.variants = enc(d.variants);
        card.dataset.prices = enc(d.prices);
        card.dataset.avail = enc(d.avail);
        card.dataset.title = d.title;
        card.dataset.vendor = d.vendor;
        card.dataset.image = d.image;
        card.dataset.tags = d.tags || '';
        card.dataset.search = (d.vendor + ' ' + d.title + ' ' + (d.tags || '').replace(/\|/g, ' ')).toLowerCase();
        const img = d.image ? `<img src="${d.image}&width=400" alt="" loading="lazy" width="400" height="400">` : '';
        card.innerHTML =
          `<button type="button" class="otb__pick" data-otb-pick aria-pressed="false">` +
          `<span class="otb__media">${img}<span class="otb__badge" aria-hidden="true">✓</span></span>` +
          `<span class="otb__info">` +
          `<span class="otb__vendor">${this.escape(d.vendor)}</span>` +
          `<span class="otb__name">${this.escape(d.title)}</span>` +
          `<span class="otb__state" data-otb-state>${this.dataset.labelAdd || 'Vælg'}</span>` +
          `</span></button>`;
        return card;
      }

      money(cents) {
        const amount = (parseInt(cents, 10) || 0) / 100;
        const da = (min, max) =>
          new Intl.NumberFormat('da-DK', { minimumFractionDigits: min, maximumFractionDigits: max }).format(amount);
        const en = (min, max) =>
          new Intl.NumberFormat('en-US', { minimumFractionDigits: min, maximumFractionDigits: max }).format(amount);
        return this.moneyFormat
          .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, da(0, 0))
          .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, da(2, 2))
          .replace(/\{\{\s*amount_no_decimals_with_space_separator\s*\}\}/g, da(0, 0).replace(/\./g, ' '))
          .replace(/\{\{\s*amount_with_space_separator\s*\}\}/g, da(2, 2).replace(/\./g, ' '))
          .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, en(0, 0))
          .replace(/\{\{\s*amount\s*\}\}/g, en(2, 2));
      }

      totalCents() {
        if (!this.size) return 0;
        if (this.selected.length) {
          return this.selected.reduce((sum, s) => sum + (parseInt(s.prices[this.size], 10) || 0), 0);
        }
        return (this.sizes[this.size].unit || 0) * this.slots;
      }

      renderTotal() {
        if (!this.priceHost) return;
        if (!this.size) {
          this.priceHost.innerHTML = '';
          return;
        }
        const label = this.priceHost.dataset.totalLabel || '';
        this.priceHost.innerHTML =
          (label ? `<span class="otb__price-label">${this.escape(label)}</span>` : '') +
          `<span class="otb__price-row"><span class="otb__price-now">${this.money(this.totalCents())}</span></span>`;
      }

      escape(s) {
        const d = document.createElement('div');
        d.textContent = s == null ? '' : s;
        return d.innerHTML;
      }

      /* ---------- filters ---------- */

      buildFilters() {
        if (!this.filtersHost) return;
        const counts = new Map();
        this.cards.forEach((c) => {
          const values =
            this.filterBy === 'vendor'
              ? [(c.dataset.vendor || '').trim()]
              : (c.dataset.tags || '').split('|').map((t) => t.trim());
          values.filter(Boolean).forEach((v) => counts.set(v, (counts.get(v) || 0) + 1));
        });
        const min = parseInt(this.dataset.tagMin, 10) || 3;
        const max = parseInt(this.dataset.tagMax, 10) || 10;
        const values = Array.from(counts.entries())
          .filter(([, n]) => n >= min)
          .sort((a, b) => b[1] - a[1])
          .slice(0, max)
          .map(([v]) => v)
          .sort((a, b) => a.localeCompare(b, 'da'));
        if (!values.length) {
          this.filtersHost.innerHTML = '';
          return;
        }
        const all = this.dataset.labelAll || 'Alle';
        this.filtersHost.innerHTML =
          `<button type="button" class="otb__chip" data-otb-tag="" aria-pressed="true">${this.escape(all)}</button>` +
          values
            .map(
              (v) =>
                `<button type="button" class="otb__chip" data-otb-tag="${this.escape(v)}" aria-pressed="false">${this.escape(v)}</button>`
            )
            .join('');
      }

      applyFilter() {
        let shown = 0;
        this.cards.forEach((card) => {
          let inSize = true;
          if (this.size) {
            const d = this.cardData(card);
            inSize = !!(d.variants && d.variants[this.size]) && d.avail[this.size] !== '0';
          }
          let matchesTag = !this.activeTag;
          if (!matchesTag) {
            matchesTag =
              this.filterBy === 'vendor'
                ? (card.dataset.vendor || '').trim() === this.activeTag
                : (card.dataset.tags || '')
                    .split('|')
                    .map((t) => t.trim())
                    .indexOf(this.activeTag) !== -1;
          }
          const matchesText = !this.query || (card.dataset.search || '').indexOf(this.query) !== -1;
          const visible = inSize && matchesTag && matchesText;
          card.hidden = !visible;
          if (visible) shown++;
        });
        if (this.emptyEl) this.emptyEl.hidden = shown !== 0;
      }

      /* ---------- selection ---------- */

      bind() {
        this.addEventListener('click', (e) => {
          const size = e.target.closest('[data-otb-size]');
          if (size) {
            e.preventDefault();
            if (size.dataset.available === 'false') return;
            this.chooseSize(size.dataset.otbSize);
            if (this.step2 && this.dataset.scrollOnSize !== 'false') {
              this.step2.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
            return;
          }
          if (e.target.closest('[data-otb-submit]')) {
            e.preventDefault();
            this.addToCart();
            return;
          }
          const pick = e.target.closest('[data-otb-pick]');
          if (pick) {
            e.preventDefault();
            this.toggle(pick.closest('.otb__card'));
            return;
          }
          const slot = e.target.closest('[data-otb-slot-filled]');
          if (slot) {
            e.preventDefault();
            this.remove(slot.dataset.handle);
            return;
          }
          if (e.target.closest('[data-otb-clear]')) {
            e.preventDefault();
            this.selected = [];
            this.sync();
            return;
          }
          const chip = e.target.closest('[data-otb-tag]');
          if (chip) {
            e.preventDefault();
            this.activeTag = chip.dataset.otbTag;
            this.filtersHost.querySelectorAll('[data-otb-tag]').forEach((c) => {
              c.setAttribute('aria-pressed', c === chip ? 'true' : 'false');
            });
            this.applyFilter();
          }
        });

        if (this.searchInput) {
          let t;
          this.searchInput.addEventListener('input', () => {
            clearTimeout(t);
            t = setTimeout(() => {
              this.query = this.searchInput.value.trim().toLowerCase();
              this.applyFilter();
            }, 120);
          });
        }
      }

      toggle(card) {
        if (!card || !this.size) return;
        const handle = card.dataset.handle;
        if (this.selected.some((s) => s.handle === handle)) {
          this.remove(handle);
          return;
        }
        if (this.selected.length >= this.slots) return;
        const d = this.cardData(card);
        if (!d.variants || !d.variants[this.size] || d.avail[this.size] === '0') return;
        this.selected.push(d);
        this.sync();
      }

      remove(handle) {
        this.selected = this.selected.filter((s) => s.handle !== handle);
        this.sync();
      }

      sync() {
        const chosen = new Set(this.selected.map((s) => s.handle));
        const full = this.selected.length >= this.slots;
        this.cards.forEach((card) => {
          const isSel = chosen.has(card.dataset.handle);
          card.classList.toggle('is-selected', isSel);
          card.classList.toggle('is-disabled', full && !isSel);
          const btn = card.querySelector('[data-otb-pick]');
          if (btn) btn.setAttribute('aria-pressed', isSel ? 'true' : 'false');
          const state = card.querySelector('[data-otb-state]');
          if (state) state.textContent = isSel ? this.dataset.labelAdded || 'Valgt' : this.dataset.labelAdd || 'Vælg';
        });
        this.renderSlots();
        this.renderTotal();
        this.renderSubmit();
      }

      renderSlots() {
        if (!this.slotsHost) return;
        let html = '';
        for (let i = 0; i < this.slots; i++) {
          const s = this.selected[i];
          if (s) {
            const thumb = s.image ? `<img class="otb__slot-thumb" src="${s.image}&width=80" alt="" loading="lazy">` : '';
            html +=
              `<button type="button" class="otb__slot is-filled" data-otb-slot-filled data-handle="${this.escape(s.handle)}" ` +
              `aria-label="${this.escape((this.dataset.labelRemove || 'Fjern') + ' ' + s.title)}">` +
              `${thumb}<span class="otb__slot-label">${this.escape(s.title)}</span><span class="otb__slot-x" aria-hidden="true">×</span></button>`;
          } else {
            html += `<span class="otb__slot">${i + 1}</span>`;
          }
        }
        this.slotsHost.innerHTML = html;
      }

      renderSubmit() {
        const left = this.slots - this.selected.length;
        const ready = !!this.size && left <= 0;
        if (this.submit) {
          this.submit.disabled = !ready;
          this.submit.setAttribute('aria-disabled', ready ? 'false' : 'true');
        }
        if (this.submitText) {
          let tpl;
          if (!this.size) tpl = this.dataset.labelPickSize || 'Vælg størrelse først';
          else if (ready) tpl = this.dataset.labelReady || 'Læg i kurv';
          else tpl = this.dataset.labelRemaining || 'Vælg [n] mere';
          this.submitText.textContent = tpl.replace('[n]', String(left));
        }
        if (this.clearBtn) this.clearBtn.hidden = this.selected.length === 0;
        const counter = this.querySelector('[data-otb-counter]');
        if (counter) {
          const tpl = this.dataset.labelCounter || '[a] af [b] valgt';
          counter.textContent = tpl.replace('[a]', String(this.selected.length)).replace('[b]', String(this.slots));
        }
      }

      /* Add the chosen fragrances as real line items, tied together as one box. */
      addToCart() {
        if (!this.size || this.selected.length < this.slots || this._busy) return;
        this._busy = true;
        this.setError('');
        this.submit.classList.add('loading');
        const spinner = this.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        const sizeLabel = this.sizes[this.size].label;
        const boxId = 'TB-' + Date.now().toString(36).toUpperCase();
        const boxName = `${this.dataset.boxName || 'Tester Box'} ${this.slots} × ${sizeLabel}`;

        const items = this.selected.map((s, i) => ({
          id: parseInt(s.variants[this.size], 10),
          quantity: 1,
          properties: {
            [boxName]: `${this.propPrefix} ${i + 1} af ${this.slots}`,
            _otb_box: boxId,
            _otb_size: sizeLabel,
          },
        }));

        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const body = { items: items };
        if (cart && typeof cart.getSectionsToRender === 'function') {
          body.sections = cart.getSectionsToRender().map((s) => s.id);
          body.sections_url = window.location.pathname;
          if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
        }

        fetch(`${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
          body: JSON.stringify(body),
        })
          .then((r) => r.json())
          .then((res) => {
            if (res.status || res.errors) {
              this.setError(res.description || res.message || '');
              return;
            }
            if (cart && typeof cart.renderContents === 'function') {
              cart.renderContents(res);
              if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
            } else {
              window.location = (window.routes && window.routes.cart_url) || '/cart';
              return;
            }
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'obtino-tester-box', cartData: res });
            }
            this.selected = [];
            this.sync();
          })
          .catch(() => this.setError(this.dataset.labelError || ''))
          .finally(() => {
            this._busy = false;
            this.submit.classList.remove('loading');
            if (spinner) spinner.classList.add('hidden');
          });
      }

      setError(msg) {
        if (!this.errorEl) return;
        this.errorEl.textContent = msg || '';
        this.errorEl.hidden = !msg;
      }
    }
  );
}
